// Dungeon viewport + entity draw pass (executeScene-only).

import type { DungeonFrameCtx } from '../core/dungeon-update';
import { G, rooms } from '../core/state';
import {
  PLAYERW,
  PLAYERH,
  SCREEN_W,
  SCREEN_H,
  SCREEN_CX,
  SCREEN_CY,
  TILE,
  S,
} from '../core/constants';
import { getImg, getAssetScale } from './assets';
import { screen, blitGame, DrawCircle, Arc } from './surface';
import { RandomDir, rand as rnd } from '../core/rng';
import {
  DrawLevel,
  DrawPlayer,
  DrawShield,
  DrawCircuit,
  DrawArtifacts,
  ScrollTo,
} from '../core/game-draw';
import { drawHudScene } from './scene-hud';
import { DrawEntities } from '../core/demon';
import { getActiveWorld } from '../core/world';
import { drawDataEntities } from '../core/entity-render';
import {
  DrawBossHP,
  DrawPowerObject,
  DrawArtifactOverhead,
  BossDialog,
} from '../core/boss';
import { DisplayAutomap } from '../core/gamemap';
import { Get } from '../core/mapgen';
import { computeCompassDirections, drawCompassOverlay } from '../core/compass';
import { isGpuRenderer } from './render-config';
import { isHudDomActive, updateHud, updateSpecialTilePrompt, isHudPromptActive } from '../ui/hud';
import { getLayoutCamera, setDungeonGpuActive } from './display';
import {
  beginGpuFrame,
  endGpuFrame,
  drawTilemapGpu,
  drawPlayerGpu,
  drawShieldGpu,
  drawCircuitArcsGpu,
  drawCircleGpu,
  drawCircleExGpu,
} from './gpu/renderer';
import { installGpuDrawBridge, uninstallGpuDrawBridge } from './gpu/blit-bridge';
import { LEGACY_VISIBLE_TILES_X, LEGACY_VISIBLE_TILES_Y, scrollForPlayerCenter } from './camera';

const M_PI = Math.PI;
const tr = Math.trunc;

let agateSurf: import('../render/surface').Surface | null = null;

function scrollCameraAware(extendedFov: boolean): void {
  const cam = getLayoutCamera(G.scroll_x, G.scroll_y);
  const view = extendedFov ? cam : { viewW: SCREEN_W, viewH: SCREEN_H };
  const target = scrollForPlayerCenter(G.player_x, G.player_y, PLAYERW, PLAYERH, view);
  ScrollTo(target.x, target.y);
}

function renderDungeonFrameGpu(ctx: DungeonFrameCtx): void {
  const cam = getLayoutCamera(G.scroll_x, G.scroll_y);
  beginGpuFrame(cam);
  installGpuDrawBridge(cam);

  scrollCameraAware(true);
  cam.baseGX = tr(G.scroll_x / TILE);
  cam.baseGY = tr(G.scroll_y / TILE);
  cam.tileOffX = ((G.scroll_x % TILE) + TILE) % TILE;
  cam.tileOffY = ((G.scroll_y % TILE) + TILE) % TILE;

  drawTilemapGpu(cam, 1, 1);

  const playerScreenX = Math.round(cam.viewW / 2) - tr(PLAYERW / 2);
  const playerScreenY = Math.round(cam.viewH / 2) - tr(PLAYERH / 2);

  if (G.player_dying === 0) {
    drawShieldGpu(cam);
    if (G.magic_circuit > 0 && G.circuit_release === 0) {
      let arcdir = RandomDir();
      const n_arcs = 1 + tr(((tr(G.circuit_size / 200) + 2) * G.magic_circuit) / G.circuit_size);
      drawCircuitArcsGpu(cam, arcdir, n_arcs);
    }
    drawPlayerGpu(cam, playerScreenX, playerScreenY, G.player_dir, tr(G.player_wlk / G.wlk_wait));
  } else if (ctx.t % 2 === 0) {
    drawPlayerGpu(cam, playerScreenX, playerScreenY, G.player_dir, tr(G.player_wlk / G.wlk_wait));
  }

  DrawEntities();
  drawDataEntities(getActiveWorld());

  if (G.boss_fight_mode === 2) DrawBossHP(100);
  if (rooms[G.player_room].room_type === 5) DrawPowerObject();
  if (rooms[G.player_room].room_type === 6 && G.current_boss === 3) DrawPowerObject();
  if (rooms[G.player_room].room_type === 4 && G.player_room % 1000 === 999) DrawPowerObject();

  if (G.player_room === G.agate_knife_loc) {
    const room_x = rooms[G.player_room].x * TILE + TILE;
    const room_y = rooms[G.player_room].y * TILE + TILE;
    const room_w = rooms[G.player_room].w * TILE - TILE * 2;
    const room_h = rooms[G.player_room].h * TILE - TILE * 2;
    if (agateSurf === null) {
      agateSurf = getImg('dat/i/agate.png');
      agateSurf.colorKey = 0;
    }
    const xpos = tr((Math.sin(ctx.agate_t * 1.33) * 0.5 + 0.5) * room_w) + room_x;
    const ypos = tr((Math.cos(ctx.agate_t * 0.7) * 0.5 + 0.5) * room_h) + room_y;
    blitGame(
      agateSurf,
      null,
      screen,
      xpos - S(16) - G.scroll_x,
      ypos - S(16) - G.scroll_y,
      getAssetScale('dat/i/agate.png')
    );
  }

    if (G.opening_door_i > 0) {
      DrawArtifactOverhead(G.opening_door_n);
      for (let i = 0; i < 5; i++) {
        const j = i * S(50) - S(250) + G.opening_door_i * S(5);
        if (j > 0) {
          drawCircleGpu(G.player_x - G.scroll_x, G.player_y - G.scroll_y, j, 255);
        }
      }
    }

    if (G.circuit_release > 0) {
      drawCircleGpu(
        Math.round(cam.viewW / 2) + (G.release_x - G.player_x),
        Math.round(cam.viewH / 2) + (G.release_y - G.player_y),
        tr((G.circuit_release * G.release_range) / 20),
        tr(Math.sin(G.circuit_release / 20.0) * 127 + 127)
      );
    }

  uninstallGpuDrawBridge();
  endGpuFrame();
}

function renderDungeonFrameSoftware(ctx: DungeonFrameCtx): void {
  const cam = getLayoutCamera(G.scroll_x, G.scroll_y);
  const playerScreenX = Math.round(cam.viewW / 2) - tr(PLAYERW / 2);
  const playerScreenY = Math.round(cam.viewH / 2) - tr(PLAYERH / 2);
  const legacyPlayerX = SCREEN_CX - PLAYERW / 2;
  const legacyPlayerY = SCREEN_CY - PLAYERH / 2;
  const useExtendedView = cam.viewW > SCREEN_W || cam.viewH > SCREEN_H;
  const drawPlayerX = useExtendedView ? playerScreenX : legacyPlayerX;
  const drawPlayerY = useExtendedView ? playerScreenY : legacyPlayerY;
  const shieldCenterX = useExtendedView ? Math.round(cam.viewW / 2) : SCREEN_CX;
  const shieldCenterY = useExtendedView ? Math.round(cam.viewH / 2) : SCREEN_CY;
  const circuitCenterX = useExtendedView ? Math.round(cam.viewW / 2) : SCREEN_CX;
  const circuitCenterY = useExtendedView ? Math.round(cam.viewH / 2) : SCREEN_CY;
  const tilesX = useExtendedView ? cam.visibleTilesX : LEGACY_VISIBLE_TILES_X;
  const tilesY = useExtendedView ? cam.visibleTilesY : LEGACY_VISIBLE_TILES_Y;

  if (!G.map_enabled) {
    scrollCameraAware(useExtendedView);
    DrawLevel(G.scroll_x, G.scroll_y, 1, 1, tilesX, tilesY);

    if (G.player_dying === 0) {
      DrawShield(shieldCenterX, shieldCenterY);
      if (G.magic_circuit > 0 && G.circuit_release === 0) {
        let arcdir = RandomDir();
        const n_arcs = 1 + tr(((tr(G.circuit_size / 200) + 2) * G.magic_circuit) / G.circuit_size);
        for (let i = 0; i < n_arcs; i++) {
          Arc(
            G.player_x - G.scroll_x + tr(PLAYERW / 2),
            G.player_y - G.scroll_y + tr(PLAYERH / 2),
            G.circuit_range,
            arcdir
          );
          arcdir += ((rnd() % 16) / 16.0) * ((M_PI * 2) / n_arcs);
        }
      }
      DrawPlayer(drawPlayerX, drawPlayerY, G.player_dir, tr(G.player_wlk / G.wlk_wait));
    } else if (ctx.t % 2 === 0) {
      DrawPlayer(drawPlayerX, drawPlayerY, G.player_dir, tr(G.player_wlk / G.wlk_wait));
    }

    DrawEntities();
  drawDataEntities(getActiveWorld());

    if (G.boss_fight_mode === 2) DrawBossHP(100);
    if (rooms[G.player_room].room_type === 5) DrawPowerObject();
    if (rooms[G.player_room].room_type === 6 && G.current_boss === 3) DrawPowerObject();
    if (rooms[G.player_room].room_type === 4 && G.player_room % 1000 === 999) DrawPowerObject();

    if (G.player_room === G.agate_knife_loc) {
      const room_x = rooms[G.player_room].x * TILE + TILE;
      const room_y = rooms[G.player_room].y * TILE + TILE;
      const room_w = rooms[G.player_room].w * TILE - TILE * 2;
      const room_h = rooms[G.player_room].h * TILE - TILE * 2;
      if (agateSurf === null) {
        agateSurf = getImg('dat/i/agate.png');
        agateSurf.colorKey = 0;
      }
      const xpos = tr((Math.sin(ctx.agate_t * 1.33) * 0.5 + 0.5) * room_w) + room_x;
      const ypos = tr((Math.cos(ctx.agate_t * 0.7) * 0.5 + 0.5) * room_h) + room_y;
      blitGame(
        agateSurf,
        null,
        screen,
        xpos - S(16) - G.scroll_x,
        ypos - S(16) - G.scroll_y,
        getAssetScale('dat/i/agate.png')
      );
    }

    if (G.opening_door_i > 0) {
      DrawArtifactOverhead(G.opening_door_n);
      for (let i = 0; i < 5; i++) {
        const j = i * S(50) - S(250) + G.opening_door_i * S(5);
        if (j > 0) DrawCircle(G.player_x - G.scroll_x, G.player_y - G.scroll_y, j, 255);
      }
    }

    if (G.circuit_release > 0) {
      DrawCircle(
        G.release_x - G.player_x + circuitCenterX,
        G.release_y - G.player_y + circuitCenterY,
        tr((G.circuit_release * G.release_range) / 20),
        tr(Math.sin(G.circuit_release / 20.0) * 127 + 127)
      );
    }
  }

  if (G.map_enabled) DisplayAutomap();
  if (G.boss_dlg !== 0 && !G.game_paused) BossDialog();
}

export function renderDungeonFrame(ctx: DungeonFrameCtx): void {
  const useGpu = isGpuRenderer() && !G.map_enabled;
  setDungeonGpuActive(useGpu);

  if (useGpu) {
    renderDungeonFrameGpu(ctx);
  } else {
    renderDungeonFrameSoftware(ctx);
  }

  if (G.map_enabled) DisplayAutomap();
  if (G.boss_dlg !== 0 && !G.game_paused) BossDialog();
}

export function renderDungeonOverlays(ctx: DungeonFrameCtx, specialTile: (x: number, y: number) => void): void {
  const tileX = tr((G.player_x + PLAYERW / 2) / TILE);
  const tileY = tr((G.player_y + PLAYERH / 2) / TILE);
  if (Get(tileX, tileY) === 53 && isGpuRenderer()) {
    const dirs = computeCompassDirections();
    if (dirs) {
      const cam = getLayoutCamera(G.scroll_x, G.scroll_y);
      const screenX = Math.round(cam.viewW / 2);
      const screenY = Math.round(cam.viewH / 2);
      drawCompassOverlay(screenX, screenY, dirs, drawCircleGpu, drawCircleExGpu);
    }
  }

  if (isHudDomActive()) {
    updateHud(ctx.t);
    DrawArtifacts();
  } else {
    drawHudScene(ctx.t);
    DrawCircuit();
    DrawArtifacts();
    if (isHudPromptActive()) updateSpecialTilePrompt(ctx.t);
  }
  specialTile(tr((G.player_x + PLAYERW / 2) / TILE), tr((G.player_y + PLAYERH / 2) / TILE));
}
